<template>
  <div class="flex flex-col gap-y-4 text-left" data-testid="send-record-step-1">
    <!-- Cabeçalho de contexto (balão ②): vem da vaga clicada e nunca é editável aqui. -->
    <div
      class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
      data-testid="send-record-context-card"
    >
      <div class="flex items-start justify-between gap-2">
        <p class="font-bold text-textPrimary">{{ station?.doctorName || 'Plantão selecionado' }}</p>
        <span :class="statusChipClass" data-testid="send-record-context-status">
          {{ statusLabel }}
        </span>
      </div>
      <p class="text-xs text-neutral50 mt-0.5">{{ contextLine }}</p>
      <p v-if="missingLegLabel" class="text-xs text-neutral50 mt-1">{{ missingLegLabel }}</p>
    </div>

    <!--
      As cinco opções dos dois cards formam UM único grupo de rádio (mesmo `name`):
      a escolha é uma só — marcar "Folha" desmarca "Jornada" e vice-versa. Dois
      grupos separados deixariam marcar uma opção em cada card, que é exatamente o
      estado que `category` não sabe representar.
    -->
    <fieldset class="border-0 p-0 m-0">
      <legend class="sr-only">O que você quer enviar?</legend>

      <div class="grid gap-3 md:grid-cols-2">
        <!-- Registro de Plantão: reencaminha ao wizard que já existe (balão ③). -->
        <div
          class="rounded-lg border p-3 flex flex-col gap-2"
          :class="category === 'FOLHA' ? 'border-primary bg-primary5/30' : 'border-gray-200'"
        >
          <p class="font-bold text-textPrimary">Registro de Plantão</p>
          <p class="text-xs text-neutral50">
            Folha assinada na unidade ou registro feito por app externo.
          </p>
          <label
            v-for="option in sheetOptions"
            :key="option.value"
            class="flex items-center gap-2 text-left text-sm rounded border px-2 py-1.5"
            :class="[
              sheetBlockedReason
                ? 'border-gray-200 text-neutralLight cursor-not-allowed'
                : 'cursor-pointer',
              !sheetBlockedReason && isSheetSelected(option.value)
                ? 'border-primary text-primary30 font-bold'
                : !sheetBlockedReason
                  ? 'border-gray-200 text-textPrimary'
                  : ''
            ]"
            :data-testid="`send-record-category-sheet-${option.value}`"
          >
            <input
              type="radio"
              :name="CATEGORY_RADIO_GROUP"
              :class="sheetBlockedReason ? 'cursor-not-allowed' : 'accent-primary'"
              :value="option.value"
              :checked="!sheetBlockedReason && isSheetSelected(option.value)"
              :disabled="Boolean(sheetBlockedReason) || undefined"
              :aria-describedby="sheetBlockedReason ? SHEET_BLOCKED_HINT_ID : undefined"
              :data-testid="`send-record-category-sheet-${option.value}-input`"
              @change="chooseSheet(option.value)"
            />
            <span>
              {{ option.label }}
              <span v-if="option.hint" class="block text-xs text-neutral50">{{ option.hint }}</span>
            </span>
          </label>
          <!--
            A política vigente da escala manda: sem regra de folha, o envio morreria
            no backend DEPOIS do upload ("Nenhum container de presença encontrado
            para a escala…"). O motivo fica escrito, no lugar do erro tardio.
          -->
          <p
            v-if="sheetBlockedReason"
            :id="SHEET_BLOCKED_HINT_ID"
            class="text-xs font-bold text-[#B45F09]"
            data-testid="send-record-category-sheet-blocked"
          >
            {{ sheetBlockedReason }}
          </p>
          <p v-else class="text-xs text-neutral50">
            Ao escolher, seguimos para o envio de folha que já existe hoje.
          </p>
        </div>

        <!-- Registro de Atividade: o fluxo novo desta fase. -->
        <div
          class="rounded-lg border p-3 flex flex-col gap-2"
          :class="category === 'ATIVIDADE' ? 'border-primary bg-primary5/30' : 'border-gray-200'"
        >
          <p class="font-bold text-textPrimary">Registro de Atividade</p>
          <p class="text-xs text-neutral50">
            Declaração de presença com anexos, quando não houve check no app.
          </p>
          <label
            class="flex items-center gap-2 text-left text-sm rounded border px-2 py-1.5 cursor-pointer"
            :class="
              isActivitySelected
                ? 'border-primary text-primary30 font-bold'
                : 'border-gray-200 text-textPrimary'
            "
            data-testid="send-record-category-activity-JORNADA"
          >
            <input
              type="radio"
              :name="CATEGORY_RADIO_GROUP"
              class="accent-primary"
              value="JORNADA"
              :checked="isActivitySelected"
              data-testid="send-record-category-activity-JORNADA-input"
              @change="chooseActivity()"
            />
            <span>
              Jornada
              <span class="block text-xs text-neutral50">único subtipo desta fase</span>
            </span>
          </label>
          <!--
            Balão ④: "Produtividade" aparece desabilitado de propósito, para o cliente entender
            que Jornada não é o único subtipo previsto. Sem data prometida na tela. O `disabled`
            mora no input: é ele que o teclado pula e que os testes conferem.
          -->
          <label
            class="flex items-center gap-2 text-left text-sm rounded border border-gray-200 px-2 py-1.5 text-neutralLight cursor-not-allowed"
            data-testid="send-record-category-activity-PRODUTIVIDADE"
          >
            <input
              type="radio"
              :name="CATEGORY_RADIO_GROUP"
              class="cursor-not-allowed"
              value="PRODUTIVIDADE"
              disabled
              data-testid="send-record-category-activity-PRODUTIVIDADE-input"
            />
            <span>
              Produtividade
              <span class="block text-xs text-neutralLight">em breve</span>
            </span>
          </label>
          <p class="text-xs text-neutral50">
            Gera um registro por plantão e entra na mesma fila de auditoria.
          </p>
        </div>
      </div>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  CoveredLegs,
  COVERED_LEGS_LABELS,
  EffectiveRegistrationStatus,
  EFFECTIVE_REGISTRATION_STATUS_LABELS,
  DSAttendanceMonitoringType
} from '@/constants/ds-attendance.constants'
import {
  SEND_RECORD_SHEET_OPTIONS,
  isoToTimeInput,
  type ActivitySubtype,
  type SendRecordCategory,
  type SendRecordStationOption
} from './types'

/**
 * Passo 1 de M2 — a bifurcação. "Registro de Plantão" NÃO tem fluxo próprio
 * novo: escolher uma das três subopções fecha este modal e abre o wizard de
 * folha que já existe, pré-preenchido pela vaga. Zero backend novo.
 */

const props = defineProps<{
  station?: SendRecordStationOption | null
  category: SendRecordCategory | null
  sheetMonitoringType: DSAttendanceMonitoringType | null
  activitySubtype: ActivitySubtype
  /**
   * Texto do bloqueio do "Registro de Plantão" quando a política da escala não
   * aceita folha. `null` (o default, e o que um backend antigo produz) mantém as
   * duas categorias oferecidas.
   */
  sheetBlockedReason?: string | null
}>()

const emit = defineEmits<{
  'update:category': [value: SendRecordCategory]
  'update:sheetMonitoringType': [value: DSAttendanceMonitoringType]
  'update:activitySubtype': [value: ActivitySubtype]
}>()

const sheetOptions = SEND_RECORD_SHEET_OPTIONS

/** `name` compartilhado pelas cinco opções: é ele que torna a escolha única. */
const CATEGORY_RADIO_GROUP = 'send-record-category'

/** `aria-describedby` das opções de folha desabilitadas — o motivo é um texto só. */
const SHEET_BLOCKED_HINT_ID = 'send-record-category-sheet-blocked-hint'

function isSheetSelected(monitoringType: DSAttendanceMonitoringType): boolean {
  if (props.sheetBlockedReason) return false
  return props.category === 'FOLHA' && props.sheetMonitoringType === monitoringType
}

const isActivitySelected = computed(
  () => props.category === 'ATIVIDADE' && props.activitySubtype === 'JORNADA'
)

function chooseSheet(monitoringType: DSAttendanceMonitoringType): void {
  // Guarda de verdade, e não só o `disabled` do input: um clique programático
  // (teste, extensão, autofill) não pode reabrir um caminho que o backend recusa.
  if (props.sheetBlockedReason) return
  emit('update:category', 'FOLHA')
  emit('update:sheetMonitoringType', monitoringType)
}

function chooseActivity(): void {
  emit('update:category', 'ATIVIDADE')
  emit('update:activitySubtype', 'JORNADA')
}

const statusLabel = computed(() =>
  props.station?.registrationStatus
    ? EFFECTIVE_REGISTRATION_STATUS_LABELS[props.station.registrationStatus]
    : EFFECTIVE_REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.NONE]
)

const statusChipClass = computed(() => {
  const base = 'text-xs font-bold rounded-full px-2 py-0.5 whitespace-nowrap'
  switch (props.station?.registrationStatus) {
    case EffectiveRegistrationStatus.OK:
      return `${base} bg-green-100 text-green-800`
    case EffectiveRegistrationStatus.PARTIAL:
      return `${base} bg-yellow-100 text-yellow-800`
    case EffectiveRegistrationStatus.PENDING:
      return `${base} bg-blue-100 text-blue-800`
    default:
      return `${base} bg-gray-100 text-gray-700`
  }
})

const contextLine = computed(() => {
  const station = props.station
  if (!station) return ''
  const hours =
    station.expectedStart && station.expectedEnd
      ? `${isoToTimeInput(station.expectedStart)}–${isoToTimeInput(station.expectedEnd)}`
      : ''
  return [
    station.healthUnitName,
    station.scheduleName,
    formatDate(station.referenceDate ?? station.expectedStart),
    hours,
    station.shiftName
  ]
    .filter(Boolean)
    .join(' · ')
})

const missingLegLabel = computed(() => {
  const legs = props.station?.missingLegs
  if (!legs) return ''
  return legs === CoveredLegs.BOTH
    ? 'Faltam Check-in e Check-out.'
    : `Falta o ${COVERED_LEGS_LABELS[legs]}.`
})

function formatDate(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('pt-BR')
}
</script>
